import { useId } from 'react';
import { useGT } from 'gt-react';
import { Input } from 'src/components/ui/input';
import { Label } from 'src/components/ui/label';
import { Textarea } from 'src/components/ui/textarea';
import {
  LEAD_TIME_OVERRIDE_DAYS_MAX,
  LEAD_TIME_OVERRIDE_DAYS_MIN,
} from '@erp/shared-types';
import { PolicyOverrideFormWindowFields } from '../components/PolicyOverrideFormWindowFields';
import { PolicyOverrideFormActions } from '../components/PolicyOverrideFormActions';
import type { LeadTimeOverrideFormState } from './useLeadTimeOverrideForm';

function DaysHint({
  daysValue,
  daysValid,
}: {
  daysValue: number | null;
  daysValid: boolean;
}) {
  const gt = useGT();
  if (daysValue !== null && daysValid) {
    return (
      <p className="mt-0.5 text-[10px] text-gray-400">
        {gt('≈ {weeks} weeks', {
          context: 'lead time override weeks hint',
          weeks: (daysValue / 7).toFixed(1),
        })}
      </p>
    );
  }
  return (
    <p className="mt-0.5 text-[10px] text-gray-400">
      {gt('Whole days, {min} to {max}.', {
        context: 'lead time override days hint',
        min: LEAD_TIME_OVERRIDE_DAYS_MIN,
        max: LEAD_TIME_OVERRIDE_DAYS_MAX,
      })}
    </p>
  );
}

export function LeadTimeOverrideForm({
  form,
  highlightedLineCount = 1,
}: {
  form: LeadTimeOverrideFormState;
  highlightedLineCount?: number;
}) {
  const gt = useGT();
  const fieldId = useId();
  const { editing } = form;
  return (
    <>
      {editing ? (
        <p className="mb-2 text-xs text-blue-700">
          {gt(
            'Editing the {days}d total lead time override. Saving replaces it with a new row.',
            {
              context: 'lead time override edit mode notice',
              days: editing.leadTimeDays,
            },
          )}
        </p>
      ) : null}

      <div className="grid grid-cols-2 gap-2">
        <div>
          <Label htmlFor={`${fieldId}-days`} className="text-xs text-gray-500">
            {gt('Total lead time (calendar days)', {
              context: 'lead time override days field',
            })}
          </Label>
          <Input
            id={`${fieldId}-days`}
            type="number"
            inputMode="numeric"
            min={LEAD_TIME_OVERRIDE_DAYS_MIN}
            max={LEAD_TIME_OVERRIDE_DAYS_MAX}
            step={1}
            value={form.days}
            onChange={(e) => form.setDays(e.target.value)}
            aria-invalid={(form.days !== '' && !form.daysValid) || undefined}
          />
          <DaysHint daysValue={form.daysValue} daysValid={form.daysValid} />
        </div>
        <div>
          <Label
            htmlFor={`${fieldId}-reason`}
            className="text-xs text-gray-500"
          >
            {gt('Reason', { context: 'lead time override reason field' })}
          </Label>
          <Textarea
            id={`${fieldId}-reason`}
            rows={1}
            value={form.window.reason}
            onChange={(e) => form.window.setReason(e.target.value)}
          />
        </div>
        <PolicyOverrideFormWindowFields
          window={form.window}
          pending={form.pending}
        />
      </div>

      <PolicyOverrideFormActions
        editing={editing != null}
        canSubmit={form.canSubmit}
        pending={form.pending}
        onSubmit={form.submit}
        onReset={form.reset}
        bulkHighlightedCount={highlightedLineCount}
        saveLabel={gt('Save Changes', {
          context: 'lead time override submit button',
        })}
        addLabel={gt('Add Total Override', {
          context: 'lead time override submit button',
        })}
      />
    </>
  );
}
